/**
 * AAC Learning Sync - Cloud synchronization for personalized word learning
 * Syncs learned words, bigrams, and recent words to Lovable Cloud
 */

import { supabase } from "@/integrations/supabase/client";

// ==================== Types ====================

export interface LearnedWord {
  word: string;
  frequency: number;
  lastUsed: number;
  language: "he" | "en" | "ar" | "ru";
}

export interface LearnedBigram {
  [prevWord: string]: {
    [nextWord: string]: number;
  };
}

export interface UserWordLearning {
  learned_words: LearnedWord[];
  learned_bigrams: LearnedBigram;
  recent_words: { word: string; timestamp: number }[];
}

// ==================== Constants ====================

const SYNC_DEBOUNCE_MS = 5000; // 5 seconds debounce for cloud sync
let syncTimeout: ReturnType<typeof setTimeout> | null = null;
let pendingSync = false;

// ==================== Cloud Sync Functions ====================

/**
 * Load learning data from cloud for a specific user
 */
export async function loadLearningFromCloud(userId: string): Promise<UserWordLearning | null> {
  try {
    const { data, error } = await supabase
      .from("user_word_learning")
      .select("learned_words, learned_bigrams, recent_words")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      console.error("[aacLearningSync] Error loading from cloud:", error);
      return null;
    }

    if (!data) {
      return null;
    }

    // Type assertions for JSONB data
    const learnedWords = Array.isArray(data.learned_words) 
      ? (data.learned_words as unknown as LearnedWord[]) 
      : [];
    const learnedBigrams = data.learned_bigrams && typeof data.learned_bigrams === 'object' && !Array.isArray(data.learned_bigrams)
      ? (data.learned_bigrams as unknown as LearnedBigram)
      : {};
    const recentWords = Array.isArray(data.recent_words)
      ? (data.recent_words as unknown as { word: string; timestamp: number }[])
      : [];

    return {
      learned_words: learnedWords,
      learned_bigrams: learnedBigrams,
      recent_words: recentWords,
    };
  } catch (error) {
    console.error("[aacLearningSync] Exception loading from cloud:", error);
    return null;
  }
}

/**
 * Save learning data to cloud for a specific user
 */
export async function saveLearningToCloud(
  userId: string,
  data: UserWordLearning
): Promise<boolean> {
  try {
    // First try to update existing record
    const { data: existingData } = await supabase
      .from("user_word_learning")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();

    // Cast data to JSON-compatible format
    const jsonLearnedWords = JSON.parse(JSON.stringify(data.learned_words));
    const jsonLearnedBigrams = JSON.parse(JSON.stringify(data.learned_bigrams));
    const jsonRecentWords = JSON.parse(JSON.stringify(data.recent_words));

    if (existingData) {
      // Update existing record
      const { error } = await supabase
        .from("user_word_learning")
        .update({
          learned_words: jsonLearnedWords,
          learned_bigrams: jsonLearnedBigrams,
          recent_words: jsonRecentWords,
        })
        .eq("user_id", userId);

      if (error) {
        console.error("[aacLearningSync] Error updating cloud data:", error);
        return false;
      }
    } else {
      // Insert new record - use raw SQL approach via RPC or direct insert
      const insertData = {
        user_id: userId,
        learned_words: jsonLearnedWords,
        learned_bigrams: jsonLearnedBigrams,
        recent_words: jsonRecentWords,
      };
      
      const { error } = await supabase
        .from("user_word_learning")
        .insert([insertData]);

      if (error) {
        console.error("[aacLearningSync] Error inserting cloud data:", error);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error("[aacLearningSync] Exception saving to cloud:", error);
    return false;
  }
}

/**
 * Debounced sync to cloud - prevents too many writes
 */
export function scheduleSyncToCloud(
  userId: string,
  getData: () => UserWordLearning
): void {
  // Mark that we have pending changes
  pendingSync = true;

  // Clear any existing timeout
  if (syncTimeout) {
    clearTimeout(syncTimeout);
  }

  // Schedule new sync
  syncTimeout = setTimeout(async () => {
    if (!pendingSync) return;
    
    const data = getData();
    const success = await saveLearningToCloud(userId, data);
    
    if (success) {
      pendingSync = false;
    }
  }, SYNC_DEBOUNCE_MS);
}

/**
 * Force immediate sync (e.g., on logout)
 */
export async function forceSyncToCloud(
  userId: string,
  getData: () => UserWordLearning
): Promise<boolean> {
  // Clear any pending debounced sync
  if (syncTimeout) {
    clearTimeout(syncTimeout);
    syncTimeout = null;
  }

  if (!pendingSync) {
    return true; // Nothing to sync
  }

  const data = getData();
  const success = await saveLearningToCloud(userId, data);
  
  if (success) {
    pendingSync = false;
  }
  
  return success;
}

/**
 * Check if there are pending changes to sync
 */
export function hasPendingSync(): boolean {
  return pendingSync;
}

/**
 * Merge cloud data with local data
 * Takes the higher frequency for each word and merges bigrams
 */
export function mergeLearnedData(
  local: UserWordLearning,
  cloud: UserWordLearning
): UserWordLearning {
  // Merge learned words - keep higher frequency
  const wordMap = new Map<string, LearnedWord>();
  
  for (const word of local.learned_words) {
    wordMap.set(word.word, word);
  }
  
  for (const word of cloud.learned_words) {
    const existing = wordMap.get(word.word);
    if (!existing || word.frequency > existing.frequency) {
      wordMap.set(word.word, word);
    } else if (word.lastUsed > existing.lastUsed) {
      // Keep higher frequency but update lastUsed if cloud is more recent
      existing.lastUsed = word.lastUsed;
    }
  }

  // Merge bigrams - keep higher frequency for each pair
  const mergedBigrams: LearnedBigram = { ...local.learned_bigrams };
  
  for (const [prevWord, nextWords] of Object.entries(cloud.learned_bigrams)) {
    if (!mergedBigrams[prevWord]) {
      mergedBigrams[prevWord] = {};
    }
    for (const [nextWord, freq] of Object.entries(nextWords)) {
      const existingFreq = mergedBigrams[prevWord][nextWord] || 0;
      mergedBigrams[prevWord][nextWord] = Math.max(existingFreq, freq);
    }
  }

  // Merge recent words - combine and sort by timestamp, keep most recent 50
  const allRecent = [...local.recent_words, ...cloud.recent_words];
  const uniqueRecent = new Map<string, { word: string; timestamp: number }>();
  
  for (const entry of allRecent) {
    const existing = uniqueRecent.get(entry.word);
    if (!existing || entry.timestamp > existing.timestamp) {
      uniqueRecent.set(entry.word, entry);
    }
  }
  
  const mergedRecent = Array.from(uniqueRecent.values())
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 50);

  return {
    learned_words: Array.from(wordMap.values()),
    learned_bigrams: mergedBigrams,
    recent_words: mergedRecent,
  };
}
